import React, { useState, useEffect } from 'react';
import {
  TextField,
  Typography,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Divider,
  Box,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CreateUser, EditUser } from '../../../services/users';
import useSnackbar from '../../../hooks/useSnackbar';
import { CustomerType } from '../../../utils/entities/type';
import { IUser } from '../../../utils/entities/interface';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../components/atoms/Button';
import { ImageUpload } from '../../../components/atoms/UploadImage';

type Gender = 'MALE' | 'FEMALE' | 'OTHER' | '';

interface FormData {
  id?: string;
  fullName: string;
  phone: string;
  email: string;
  type: CustomerType;
  gender: Gender;
  profilePhoto: string;
}

// UK mobile: 10-digit national number, always starting with 7 (mirrors the
// backend's `^[0-9]{10}$` requirement in AdminUserValidator).
const sanitizeUkMobileDigits = (raw: string) => {
  let digits = raw.replace(/\D/g, '');
  if (digits.startsWith('0')) digits = digits.slice(1);
  return digits.slice(0, 10);
};

const isValidUkMobileDigits = (digits: string) =>
  digits.length === 10 && digits[0] === '7';

interface UserAddEditProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  customer?: IUser | null;
}
const CreateUserModal = ({
  open,
  onClose,
  onSaved,
  customer,
}: UserAddEditProps) => {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    phone: '',
    email: '',
    type: 'RETAIL',
    gender: '',
    profilePhoto: '',
  });
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);

  useEffect(() => {
    if (customer) {
      setFormData({
        id: customer._id,
        fullName: customer.name || '',
        phone: sanitizeUkMobileDigits(customer.phoneNumber || ''),
        email: customer.email || '',
        type: customer.type || 'RETAIL',
        gender: customer.gender || '',
        profilePhoto: customer.profilePhoto || '',
      });
    } else {
      setFormData({
        fullName: '',
        phone: '',
        email: '',
        type: 'RETAIL',
        gender: '',
        profilePhoto: '',
      });
    }
    setPhoneTouched(false);
  }, [customer]);

  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData((prev) => ({ ...prev, phone: sanitizeUkMobileDigits(value) }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isEditMode = Boolean(formData.id);
  const phoneInvalid =
    !isEditMode &&
    phoneTouched &&
    formData.phone.length > 0 &&
    !isValidUkMobileDigits(formData.phone);

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!isEditMode && !isValidUkMobileDigits(formData.phone)) {
      setPhoneTouched(true);
      showSnackbar(
        'Enter a valid UK mobile number (10 digits, starting with 7)',
        'error',
      );
      return;
    }
    try {
      let status;
      if (formData.id) {
        status = await EditUser({
          phoneNumber: formData.phone,
          countryCode: '+44',
          userName: formData.fullName,
          type: formData.type,
          email: formData.email,
          userId: formData.id,
          gender: formData.gender,
          profilePhoto: formData.profilePhoto,
        });
      } else {
        status = await CreateUser({
          phoneNumber: formData.phone,
          countryCode: '+44',
          userName: formData.fullName,
          type: formData.type,
          email: formData.email,
          gender: formData.gender,
          profilePhoto: formData.profilePhoto,
        });
      }

      if (status === 200 || status === 201) {
        showSnackbar(
          `User ${formData.id ? 'updated' : 'created'} successfully!`,
          'success',
        );
        setTimeout(() => {
          onClose();
          if (onSaved) onSaved();
        }, 1500);
      } else {
        showSnackbar(
          `Error: Failed to ${formData.id ? 'update' : 'create'} user`,
          'error',
        );
      }
    } catch (error: any) {
      showSnackbar(`Error: ${error.message}`, 'error');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      sx={{ borderRadius: 10, overflow: 'hidden' }} // Ensuring rounded corners
      PaperProps={{
        sx: {
          borderRadius: 10,
          overflow: 'hidden', // Ensures the background doesn't leak
        },
      }}
    >
      <Box
        sx={{
          border: '1px solid #7B1FA2', // Add border here
          borderRadius: 10,
          overflow: 'hidden', // Prevent border from affecting layout
        }}
      >
        {SnackbarComponent}
        <DialogTitle
          sx={{
            fontSize: '22px',
            fontWeight: 'bold',
            color: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#181818',
            padding: '16px',
          }}
        >
          {formData.id ? 'Edit User' : 'Create User'}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ backgroundColor: '#181818', padding: '24px' }}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Typography gutterBottom>Full Name</Typography>
              <TextField
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                fullWidth
                required
                sx={{
                  backgroundColor: '#222',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography gutterBottom> Contact Number</Typography>
              <TextField
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={() => setPhoneTouched(true)}
                fullWidth
                required
                disabled={isEditMode}
                placeholder="7123 456789"
                error={phoneInvalid}
                helperText={
                  isEditMode
                    ? "Phone number can't be changed"
                    : phoneInvalid
                      ? 'Enter a valid UK mobile number (10 digits, starting with 7)'
                      : ''
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography
                        sx={{
                          color: '#9aa',
                          fontWeight: 600,
                          pr: 0.5,
                          borderRight: '1px solid #444',
                        }}
                      >
                        +44
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                inputProps={{ maxLength: 11, inputMode: 'numeric' }}
                sx={{
                  backgroundColor: '#222',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography gutterBottom>Email Address</Typography>
              <TextField
                name="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                sx={{
                  backgroundColor: '#222',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography gutterBottom>Customer Type</Typography>
              <Select
                name="type"
                value={formData.type}
                onChange={handleChange}
                fullWidth
                sx={{
                  backgroundColor: '#222',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              >
                <MenuItem value="RETAIL">Retail</MenuItem>
                <MenuItem value="HNI">HNI</MenuItem>
                <MenuItem value="SME">SME</MenuItem>
                <MenuItem value="LARGE_SCALE">Large Scale</MenuItem>
                <MenuItem value="OEM">OEM</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={6}>
              <Typography gutterBottom>Gender</Typography>
              <Select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                displayEmpty
                fullWidth
                sx={{
                  backgroundColor: '#222',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              >
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="MALE">Male</MenuItem>
                <MenuItem value="FEMALE">Female</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={6}>
              <Typography gutterBottom>Profile Photo</Typography>
              <ImageUpload
                imageUrl={formData.profilePhoto}
                onChange={(url) =>
                  setFormData((prev) => ({ ...prev, profilePhoto: url }))
                }
                onRemove={() =>
                  setFormData((prev) => ({ ...prev, profilePhoto: '' }))
                }
                setLoader={setPhotoUploading}
                uploadLabel="Upload profile photo"
                fileId="user-profile-photo"
                type="profile"
                folderType={3}
                disabled={photoUploading}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#181818', padding: '16px' }}>
          <DialogActions sx={{ backgroundColor: '#181818', padding: '16px' }}>
            <SecondaryButton title="Cancel" onClick={onClose} />
            <PrimaryButton title="Save" onClick={(e) => handleSubmit(e)} />
          </DialogActions>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CreateUserModal;
